import { configureStore, createSlice } from '@reduxjs/toolkit';
import authReducer from './slices/authSlice';
import socketReducer from './slices/socketSlice';

// Enhanced monitoring slice with branch support
const monitoringSlice = createSlice({
  name: 'monitoring',
  initialState: {
    roomStatus: {},
    userActivityLogs: [],
    actionReports: [],
    branchStats: {},
    deviceStatus: {},
  },
  reducers: {
    updateRoomStatus(state, action) {
      const { roomNumber, status, branchId } = action.payload;
      if (!state.roomStatus[branchId]) {
        state.roomStatus[branchId] = {};
      }
      state.roomStatus[branchId][roomNumber] = status;
    },
    addUserActivityLog(state, action) {
      state.userActivityLogs.unshift({
        ...action.payload,
        timestamp: new Date().toISOString(),
      });
      // Keep only the last 1000 logs
      if (state.userActivityLogs.length > 1000) {
        state.userActivityLogs = state.userActivityLogs.slice(0, 1000);
      }
    },
    addActionReport(state, action) {
      state.actionReports.unshift({
        ...action.payload,
        timestamp: new Date().toISOString(),
      });
      // Keep only the last 500 reports
      if (state.actionReports.length > 500) {
        state.actionReports = state.actionReports.slice(0, 500);
      }
    },
    updateBranchStats(state, action) {
      const { branchId, stats } = action.payload;
      state.branchStats[branchId] = {
        ...state.branchStats[branchId],
        ...stats,
        lastUpdated: new Date().toISOString(),
      };
    },
    updateDeviceStatus(state, action) {
      const { deviceId, status, branchId } = action.payload;
      if (!state.deviceStatus[branchId]) {
        state.deviceStatus[branchId] = {};
      }
      state.deviceStatus[branchId][deviceId] = {
        ...status,
        lastUpdated: new Date().toISOString(),
      };
    },
    clearLogs(state) {
      state.userActivityLogs = [];
      state.actionReports = [];
    },
    clearBranchData(state, action) {
      const { branchId } = action.payload;
      if (state.roomStatus[branchId]) {
        delete state.roomStatus[branchId];
      }
      if (state.branchStats[branchId]) {
        delete state.branchStats[branchId];
      }
      if (state.deviceStatus[branchId]) {
        delete state.deviceStatus[branchId];
      }
    },
  },
});

export const {
  updateRoomStatus,
  addUserActivityLog,
  addActionReport,
  updateBranchStats,
  updateDeviceStatus,
  clearLogs,
  clearBranchData,
} = monitoringSlice.actions;

export const store = configureStore({
  reducer: {
    auth: authReducer,
    monitoring: monitoringSlice.reducer,
    socket: socketReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST'],
      },
    }),
});

// Type definitions for TypeScript users (commented out for JavaScript build):
// export type RootState = ReturnType<typeof store.getState>;
// export type AppDispatch = typeof store.dispatch;
